import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { BoltOutline, FolderOutline, LightBulbOutline, SquareThreeStackOutline } from 'Components/Icons';
import {
    shouldRuleGroupConditions,
    shouldShowAdvancedRules,
    shouldShowCreateRule,
    shouldShowNetworkRules
} from 'Selectors';
import { RoleService } from 'Services';

import MultiOptionDropdown from 'Components/MultiOptionDropdown';

type AddNewRuleButtonProps = {
    tenantGroupId?: string;
    isTenantWithLandlord?: boolean;
};

const AddNewRuleButton = ({ tenantGroupId, isTenantWithLandlord = false }: AddNewRuleButtonProps) => {
    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation();

    const navigateToOverridePage = () => {
        if (tenantGroupId) {
            history.push(`${location.pathname}/overrides`);
        } else {
            history.push(`${location.pathname}/deployed/landlord/overrides`);
        }
    };
    const options: MenuItem[] = [
        {
            visibility: shouldShowCreateRule() && RoleService.hasCreateRuleAccess,
            displayText: t('Simple Rule'),
            iconElement: <LightBulbOutline />,
            handleNavigation: () => history.push('/global-rules/create-rule')
        },
        {
            visibility: shouldShowAdvancedRules() && shouldShowCreateRule() && RoleService.hasCreateAdvancedRuleAccess,
            displayText: t('Advanced Rule'),
            iconElement: <BoltOutline />,
            handleNavigation: () => history.push(`${location.pathname}/create-advanced-rule`)
        },
        {
            visibility: isTenantWithLandlord && shouldShowNetworkRules(),
            displayText: t('Override'),
            iconElement: <SquareThreeStackOutline />,
            handleNavigation: navigateToOverridePage
        },
        {
            horizontalRule: shouldRuleGroupConditions(),
            visibility: shouldRuleGroupConditions(),
            displayText: t('ruleGroupConditions.menuItem'),
            iconElement: <FolderOutline />,
            handleNavigation: () => history.push(`${location.pathname}/rule-group/create`)
        }
    ];

    const singleOverrideOption: MenuItem[] = [
        {
            visibility: true,
            displayText: t('Override'),
            iconElement: <SquareThreeStackOutline />,
            handleNavigation: navigateToOverridePage
        }
    ];

    const dropdownOptions = tenantGroupId ? singleOverrideOption : options;
    const dropdownOptionsLength = dropdownOptions.filter((option) => option.visibility).length;

    return dropdownOptionsLength > 0 ? (
        <MultiOptionDropdown
            iconElements
            id='rule-multi-option-button'
            variant={tenantGroupId ? 'primary' : 'secondary'}
            showArrows
            alignment={tenantGroupId ? 'right' : 'left'}
            baseText={t('Add New')}
            options={tenantGroupId ? singleOverrideOption : options}
        />
    ) : null;
};

export default AddNewRuleButton;
